import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /**
   * The drawer panel, relocated to a direct child of <body> on
   * connect - see #relocateDrawerToBody for why.
   * @type {HTMLElement | null}
   */
  #drawerPanel = null;

  /**
   * The drawer backdrop, relocated alongside the panel.
   * @type {HTMLElement | null}
   */
  #backdrop = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#relocateDrawerToBody();
  }

  /**
   * Safari has a real, confirmed quirk (verified on a live device via
   * Web Inspector - the drawer'''s actual box was rendering ~700px
   * below where it visually appeared, matching the page'''s scroll
   * offset): a position:sticky ancestor (our sticky site header) can
   * break position:fixed on a descendant, making it position itself
   * relative to the document instead of the viewport. Since the
   * drawer panel and backdrop live inside that sticky header, taps
   * that looked like they landed on a menu link were actually landing
   * on whatever homepage content was really at that document
   * position. Moving both to be direct children of <body> removes
   * them from the sticky ancestor entirely, so position:fixed works
   * correctly no matter how far the page has scrolled. Only the
   * drawer panel and backdrop move - the hamburger icon (<summary>)
   * stays exactly where it was in the header row.
   */
  #relocateDrawerToBody() {
    const panel = this.refs.details.querySelector(':scope > [data-header-drawer]');
    const backdrop = this.refs.details.querySelector(':scope > .menu-drawer__backdrop');

    if (panel) {
      document.body.appendChild(panel);
      this.#drawerPanel = panel;
    }
    if (backdrop) {
      document.body.appendChild(backdrop);
      this.#backdrop = backdrop;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => details.classList.add('menu-open'));

    trapFocus(details);

    if (details === this.refs.details) {
      this.#drawerPanel?.classList.add('menu-open');
      this.#backdrop?.classList.add('menu-open');
    }
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    const isRoot = details === this.refs.details;
    if (isRoot) {
      this.#drawerPanel?.classList.remove('menu-open');
      this.#backdrop?.classList.remove('menu-open');
    }

    // When closing the root drawer, its own animation now lives on
    // the relocated panel/backdrop (moved out of this element'''s
    // subtree), so wait on those directly instead of just `details`.
    const animatedTargets = isRoot
      ? [details, this.#drawerPanel, this.#backdrop].filter(Boolean)
      : details;

    onAnimationEnd(animatedTargets, () => {
      reset(details);

      if (isRoot) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
